import React, { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { InferType } from 'yup'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { theme } from '../../../../theme/theme'
import { Button, ButtonVariant } from '../../../common/Button'
import { Icon, IconChoices } from '../../../common/Icon'
import { Modal } from '../../../common/Modal'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { ModalFooter } from '../../../common/Modal/ModalFooter'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { FormControl } from '../../../common/FormControl'
import { SelectDropdown } from '../../../common/SelectDropdown'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import {
  NOTIFICATION_SETTINGS_VALUE,
  notificationSettingsOptions,
} from '../../../../helpers/notification_settings_options'
import { societyEventRemoveSchema } from '../../../../forms/schemas/society_event_remove'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { deleteEvent } from '../../../../api/events'
import { deleteBoardRoomEvent } from '../../../../api/boardroom-events'
import { IBoardRoomEventModel } from '../../../../interfaces/models/boardroom-events.interfaces'
import { IEventModel } from '../../../../interfaces/models/events.interfaces'
import { i18nSocietyType } from '../../../../i18n'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'

interface RemoveConfirmationModalProps {
  close: () => void
  show: boolean
  title: string
  description?: string
  event: IEventModel | IBoardRoomEventModel
  deleteMode?: boolean
  confirmationButtonLabel?: string
  boardRoomEvent?: boolean
}

export const RemoveEventModal = ({
  show,
  title,
  description,
  close,
  event,
  deleteMode = true,
  confirmationButtonLabel,
  boardRoomEvent,
}: RemoveConfirmationModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const navigate = useNavigate()

  const cancel = (): void => {
    close()
  }

  const { society } = useCurrentSociety()

  const { mutate: mutateDeleteEvent } = useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('createUpdateEvent.flashMessage.deleteEventSuccess')
      )
      navigate(reverseUrl('society:calendar', { id: event.societyId }))
      close()
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('createUpdateEvent.flashMessage.deleteEventFailure')
      )
    },
  })
  const { mutate: mutateDeleteBoardEvent } = useMutation({
    mutationFn: deleteBoardRoomEvent,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('createUpdateEvent.flashMessage.deleteEventSuccess')
      )

      navigate(reverseUrl('management:calendar', { id: event.societyId }))
      close()
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('createUpdateEvent.flashMessage.deleteEventFailure')
      )
    },
  })
  const onSubmit = async (
    onValid: InferType<typeof societyEventRemoveSchema>
  ): Promise<void> => {
    if (boardRoomEvent) {
      mutateDeleteBoardEvent({
        id: event._id,
        queryParameters: { notificationSetting: onValid.notificationSettings },
      })
    } else {
      mutateDeleteEvent({
        id: event._id,
        queryParameters: { notificationSetting: onValid.notificationSettings },
      })
    }
  }

  const { getErrorMessage } = useFormErrorMessage()

  const getDefaultValues = useCallback((): InferType<
    typeof societyEventRemoveSchema
  > => {
    return {
      notificationSettings:
        event?.notificationSettings as NOTIFICATION_SETTINGS_VALUE,
    }
  }, [event])

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyEventRemoveSchema),
    defaultValues: getDefaultValues(),
  })

  const getConfirmationButtonLabel = (): string => {
    if (confirmationButtonLabel) {
      return confirmationButtonLabel
    }
    return translate(deleteMode ? 'common.actions.delete' : 'common.yes')
  }

  return (
    <Modal show={show} size="sm">
      {{
        header: <ModalHeader style={theme.textVariants.lead} onClose={close} />,
        body: (
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody>
              <div className="flex items-start gap-3 items-center pb-8">
                <Icon icon={IconChoices.EXCLAMATION_ALERT} />
                <p style={theme.textVariants.h3}>{title}</p>
              </div>
              <p className="text-neutral-40" style={theme.textVariants.base}>
                {description}
              </p>
              <Controller
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <FormControl
                    label={
                      boardRoomEvent
                        ? translate('common.notificationSettings.labelBoard', {
                            context: i18nSocietyType(
                              society?.societyTypeDisplayName
                            ),
                          })
                        : translate('common.notificationSettings.label', {
                            context: i18nSocietyType(
                              society?.societyTypeDisplayName
                            ),
                          })
                    }
                    name={name}
                    error={
                      errors.notificationSettings &&
                      getErrorMessage(errors.notificationSettings)
                    }
                  >
                    <SelectDropdown
                      options={notificationSettingsOptions}
                      value={notificationSettingsOptions.find(
                        (option) => option.value === value
                      )}
                      onChange={(option) => onChange(option?.value)}
                      error={!!errors.notificationSettings}
                    />
                  </FormControl>
                )}
                name="notificationSettings"
              />
            </ModalBody>
            <ModalFooter className="flex flex-row gap-3">
              <Button
                type="button"
                label={`${translate('common.actions.cancel')}`}
                onClick={cancel}
                size="sm"
              />
              <Button
                type="submit"
                label={getConfirmationButtonLabel()}
                size={deleteMode ? 'sm' : 'sm-wide'}
                variant={ButtonVariant.DANGER}
              />
            </ModalFooter>
          </form>
        ),
      }}
    </Modal>
  )
}
